import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Option,
  Text,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  TabList,
  Tab,
  Spinner,
  Toaster,
  Toast,
  ToastTitle,
  useToastController,
  useId,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import type { OptionOnSelectData, SelectTabData, SelectTabEvent } from '@fluentui/react-components';
import { Calendar as FluentCalendar } from '@fluentui/react-calendar-compat';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  ChevronDownRegular,
  FilterRegular,
  CalendarLtrRegular,
  ConferenceRoomRegular,
  CalendarDay20Regular,
  CalendarWorkWeek20Regular,
  CalendarMonth20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { BasicButton } from '@components/common/BasicButton';
import { getRoomLayoutMeta } from '@components/spaces/SpaceCard/SpaceCard.constants';
import { useCalendarScreenStyles } from './CalendarScreen.styles';
import {
  Calendar,
  RoomCalendar,
  RoomMonthView,
  RoomWeekGrid,
  MiniCalendar,
  getHeaderLabel,
  navigateNext,
  navigatePrev,
  formatTime,
} from 'digital-workplace-calendar-module';
import { EventReviewPanel } from '@/components/planner/EventReviewPanel';
import { DeclineDialog } from '@/components/planner/DeclineDialog';
import { CancelBookingDialog } from '@/components/planner/CancelBookingDialog';
import { CancelSuccessDialog } from '@/components/planner/CancelSuccessDialog';
import { useReviewActions } from '@/hooks/useReviewActions/useReviewActions';
import { RoomFilterPanel, EMPTY_ROOM_FILTERS } from '@/layouts';
import type { RoomFilters } from '@/layouts';
import type { CalendarEvent } from '@/types/calendarTypes';
import type { CalendarView, EventDropInfo, StatusColor } from 'digital-workplace-calendar-module';
import {
  useSearchSpacesQuery,
  useGetPlannerBuildingsQuery,
  useCheckSpaceAvailabilityMutation,
} from '@/store/api/spaceConfigService/spaceConfigServiceApi';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetAllPlannerEventsQuery, usePatchEventMutation } from '@/store/api/eventService';
import { eventServiceApi } from '@/store/api/eventService/eventServiceApi';
import { getActiveAccount } from '@/services';
import { telemetry } from '@/services/telemetry';
import { EVENT_TYPES } from '@/constants';
import { format } from 'date-fns';
import { eventStateToStatus, mapEventSummariesToCalendarEvents } from '@/utils/mapCalendarEvents';
import { getStatusStyle } from '@/utils/calendarEventStyles';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  setActiveTab as setActiveTabAction,
  setView as setViewAction,
  setRoomView as setRoomViewAction,
  setDateIso,
  setSelectedRooms as setSelectedRoomsAction,
  setRoomFilterOpen,
  resetCalendarUi,
  selectCalendarActiveTab,
  selectCalendarView,
  selectCalendarRoomView,
  selectCalendarDateIso,
  selectCalendarSelectedRooms,
  selectCalendarRoomFilterOpen,
} from '@store/slices';
import type { EventSummary } from '@/store/models/events/entities';
import type {
  BaseRoomInfo,
  BaseRoomBooking,
  RoomBookingDropInfo,
} from 'digital-workplace-calendar-module';
import type { SpaceWithAvailability, BuildingId } from '@/store/models';
import type { EventFilters } from '@/layouts/EventFilterPanel';
import {
  getDateRangeForView,
  buildEventsQueryParams,
  isVisibleCalendarEvent,
  isVisibleRoomCalendarEvent,
  ROOM_CALENDAR_STATUS_FILTER,
  findSpaceById,
  getOnlyAvailableSetupCode,
  getSetupCodesForSpace,
  isSetupSelectionOverCapacity,
  mapSummariesToRoomBookings,
  resolveRoomSetupOptions,
} from './calendarScreenUtils';

type CalendarTab = 'events' | 'rooms';

const VIEW_OPTIONS: CalendarView[] = ['month', 'week', 'day'];
const ROOM_VIEW_OPTIONS: CalendarView[] = ['week', 'day'];
const CAL_TAB_OPTIONS: CalendarTab[] = ['events', 'rooms'];

const VIEW_ICONS: Record<CalendarView, React.ReactElement> = {
  day: <CalendarDay20Regular />,
  week: <CalendarWorkWeek20Regular />,
  month: <CalendarMonth20Regular />,
};

const HIGHLIGHTED_EVENT_COLOR: StatusColor = {
  bg: tokens.colorBrandBackgroundPressed,
  border: tokens.colorPaletteBlueBorderActive,
  text: tokens.colorNeutralForegroundOnBrand,
};

interface InitialCalendarParams {
  view?: CalendarView;
  roomView?: CalendarView;
  date?: Date;
  subTab?: CalendarTab;
  /** Event ID to visually highlight after a View in Calendar navigation. */
  highlightedEventId?: string;
  /** Local time (HH:mm) to scroll the day/week grid to, e.g. an event start. */
  time?: string;
}

const readCalendarParams = (params: URLSearchParams): InitialCalendarParams => {
  const viewParam = params.get('view');
  const roomViewParam = params.get('roomView');
  const dateParam = params.get('date');
  const subTabParam = params.get('subTab');
  const timeParam = params.get('time');
  const eventIdParam = params.get('eventId')?.trim();
  const result: InitialCalendarParams = {};
  if (viewParam && (VIEW_OPTIONS as string[]).includes(viewParam)) {
    result.view = viewParam as CalendarView;
  }
  if (roomViewParam && (ROOM_VIEW_OPTIONS as string[]).includes(roomViewParam)) {
    result.roomView = roomViewParam as CalendarView;
  }
  if (subTabParam && (CAL_TAB_OPTIONS as string[]).includes(subTabParam)) {
    result.subTab = subTabParam as CalendarTab;
  }
  if (timeParam && /^([01]\d|2[0-3]):[0-5]\d$/.test(timeParam)) {
    result.time = timeParam;
  }
  if (eventIdParam) {
    result.highlightedEventId = eventIdParam;
  }
  if (dateParam) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateParam);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);
      const parsed = new Date(year, month - 1, day);
      if (!Number.isNaN(parsed.getTime())) {
        result.date = parsed;
      }
    }
  }
  return result;
};

const readInitialCalendarParams = (): InitialCalendarParams => {
  if (typeof window === 'undefined') {
    return {};
  }
  return readCalendarParams(new URLSearchParams(window.location.search));
};

interface CalendarScreenProps {
  /** Event filters applied from the filter panel */
  eventFilters?: EventFilters;
  /** Callback to clear active filters */
  onClearFilters?: () => void;
  /** Optional toolbar slot rendered in the events-tab toolbar (e.g. status filter) */
  eventsFilterSlot?: ReactNode;
}

export const CalendarScreen = ({
  eventFilters,
  onClearFilters: _onClearFilters,
  eventsFilterSlot,
}: CalendarScreenProps) => {
  const styles = useCalendarScreenStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const initialCalendarParamsRef = useRef(readInitialCalendarParams());
  const [searchParams] = useSearchParams();
  const urlParams = useMemo(() => readCalendarParams(searchParams), [searchParams]);
  const didInitialResetRef = useRef(false);

  // Shared interaction state lives in the calendarUi slice. Derived values fall
  // back to URL-param defaults until the user changes something (which dispatches).
  const sliceActiveTab = useAppSelector(selectCalendarActiveTab);
  const sliceView = useAppSelector(selectCalendarView);
  const sliceRoomView = useAppSelector(selectCalendarRoomView);
  const sliceDateIso = useAppSelector(selectCalendarDateIso);
  const selectedRooms = useAppSelector(selectCalendarSelectedRooms);
  const isRoomFilterOpen = useAppSelector(selectCalendarRoomFilterOpen);

  const activeTab = (sliceActiveTab ||
    initialCalendarParamsRef.current.subTab ||
    'events') as CalendarTab;
  const view = (sliceView || initialCalendarParamsRef.current.view || 'day') as CalendarView;
  const roomView = (sliceRoomView ||
    initialCalendarParamsRef.current.roomView ||
    'day') as CalendarView;
  const date = useMemo(
    () =>
      sliceDateIso ? new Date(sliceDateIso) : (initialCalendarParamsRef.current.date ?? new Date()),
    [sliceDateIso]
  );

  // Time (from a deep-link like "View in Calendar") to scroll the day/week grid
  // to. Anchored to the event's date so navigating to a different day falls back
  // to the default top/current-time behavior.
  const scrollToTime = useMemo<Date | null>(() => {
    if (!urlParams.date || !urlParams.time) return null;
    if (
      date.getFullYear() !== urlParams.date.getFullYear() ||
      date.getMonth() !== urlParams.date.getMonth() ||
      date.getDate() !== urlParams.date.getDate()
    ) {
      return null;
    }
    const match = /^(\d{2}):(\d{2})$/.exec(urlParams.time);
    if (!match) return null;
    const d = new Date(urlParams.date);
    d.setHours(Number(match[1]), Number(match[2]), 0, 0);
    return d;
  }, [urlParams.date, urlParams.time, date]);

  const highlightedEventId = urlParams.highlightedEventId;

  // Reset slice on mount so calendar state is fresh per visit (URL params still
  // honored via the fallbacks above), then sync URL params into the slice on
  // every URL change so deep-links like ?view=day&date=YYYY-MM-DD navigate the
  // calendar even when CalendarScreen is already mounted.
  useEffect(() => {
    if (!didInitialResetRef.current) {
      dispatch(resetCalendarUi());
      didInitialResetRef.current = true;
    }
    if (urlParams.subTab) {
      dispatch(setActiveTabAction(urlParams.subTab));
    }
    if (urlParams.view) {
      dispatch(setViewAction(urlParams.view));
    }
    if (urlParams.roomView) {
      dispatch(setRoomViewAction(urlParams.roomView));
    }
    if (urlParams.date) {
      dispatch(setDateIso(urlParams.date.toISOString()));
      setMiniCalMonth(urlParams.date);
    }
  }, [dispatch, urlParams]);

  const setActiveTab = useCallback(
    (tab: CalendarTab) => dispatch(setActiveTabAction(tab)),
    [dispatch]
  );
  const setView = useCallback((v: CalendarView) => dispatch(setViewAction(v)), [dispatch]);
  const setRoomView = useCallback((v: CalendarView) => dispatch(setRoomViewAction(v)), [dispatch]);
  const setDate = useCallback((d: Date) => dispatch(setDateIso(d.toISOString())), [dispatch]);
  const setSelectedRooms = useCallback(
    (ids: string[]) => dispatch(setSelectedRoomsAction(ids)),
    [dispatch]
  );
  const setIsRoomFilterOpen = useCallback(
    (open: boolean) => dispatch(setRoomFilterOpen(open)),
    [dispatch]
  );

  const [miniCalMonth, setMiniCalMonth] = useState(
    () => initialCalendarParamsRef.current.date ?? new Date()
  );

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [roomFilters, setRoomFilters] = useState<RoomFilters>(EMPTY_ROOM_FILTERS);
  const [pendingDrop, setPendingDrop] = useState<EventDropInfo<CalendarEvent> | null>(null);
  const [pendingRoomDrop, setPendingRoomDrop] = useState<RoomBookingDropInfo<
    BaseRoomInfo,
    BaseRoomBooking
  > | null>(null);
  const [roomDropAvailability, setRoomDropAvailability] = useState<
    'idle' | 'checking' | 'available' | 'unavailable' | 'cross-building'
  >('idle');
  const [selectedRoomSetupCodes, setSelectedRoomSetupCodes] = useState<string[]>([]);
  const [isPatching, setIsPatching] = useState(false);
  const toasterId = useId('calendar-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const [patchEvent] = usePatchEventMutation();
  const [getEventById] = eventServiceApi.useLazyGetEventByIdQuery();
  const [checkSpaceAvailability] = useCheckSpaceAvailabilityMutation();

  const hasActiveRoomFilters =
    roomFilters.buildingIds.length > 0 ||
    roomFilters.roomIds.length > 0 ||
    roomFilters.roomTypes.length > 0 ||
    roomFilters.capacities.length > 0 ||
    roomFilters.amenities.length > 0 ||
    roomFilters.availability.length > 0;

  // Compute date range based on current view + date for events API
  const eventsDateRange = useMemo(() => getDateRangeForView(date, view), [view, date]);

  // Build API params: always include date range, add filters when active
  const eventsQueryParams = useMemo(
    () => buildEventsQueryParams(eventsDateRange, eventFilters),
    [eventsDateRange, eventFilters]
  );

  // Single API call: date range + filters combined.
  // Caching is disabled — every view/date/filter change must hit the network.
  const {
    data: eventsResponse,
    isFetching: isEventsFetching,
    refetch: refetchCalendarEvents,
  } = useGetAllPlannerEventsQuery(activeTab === 'events' ? eventsQueryParams : skipToken, {
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  // Map API events to CalendarEvent[] for the events tab
  const displayEvents = useMemo<CalendarEvent[]>(() => {
    if (!eventsResponse || !eventsResponse.success) return [];
    const summaries = (eventsResponse.data as unknown as EventSummary[]).filter(
      isVisibleCalendarEvent
    );
    return mapEventSummariesToCalendarEvents(summaries);
  }, [eventsResponse]);

  // Fetch planner's building access, then search spaces for those buildings.
  // The planner's building access is served from the shared, cached
  // getPlannerBuildings query (keepUnusedDataFor: 600) — reusing it here avoids a
  // second, uncached round-trip to /planner/building-access.
  const { data: plannerBuildingsData } = useGetPlannerBuildingsQuery();

  // Building-id list that drives the space search. Skip until the planner's
  // buildings have resolved to a non-empty set.
  const searchBuildingIds = plannerBuildingsData?.buildingIds?.length
    ? plannerBuildingsData.buildingIds
    : null;

  // The space search result is read from the RTK Query cache (no local mirror of
  // server data) — only the building-id list needed to drive the query is held.
  const { data: spacesSearchResponse } = useSearchSpacesQuery(
    searchBuildingIds && activeTab === 'rooms' ? { buildingIds: searchBuildingIds } : skipToken,
    {
      // Calendar must never serve stale server data — always refetch on
      // mount, focus, or reconnect.
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    }
  );
  const spacesData = useMemo<SpaceWithAvailability[]>(
    () =>
      spacesSearchResponse?.success && Array.isArray(spacesSearchResponse.data)
        ? spacesSearchResponse.data
        : [],
    [spacesSearchResponse]
  );

  // Building-name lookup for the Room name filter labels ("Building - Room").
  const buildingNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const b of plannerBuildingsData?.buildings ?? []) {
      map[b.buildingId] = b.buildingName;
    }
    return map;
  }, [plannerBuildingsData?.buildings]);

  // Compute buildingId filter for rooms: when specific rooms are selected, narrow by their building
  const roomBuildingFilter = useMemo(() => {
    if (selectedRooms.length === 0) return undefined;
    const buildingIds = new Set(
      spacesData
        .filter((s) => selectedRooms.includes(s.spaceId as string))
        .map((s) => s.buildingId as string)
    );
    // API supports single buildingId — use first one if filtered to one building
    if (buildingIds.size === 1) return [...buildingIds][0] as BuildingId;
    return undefined;
  }, [selectedRooms, spacesData]);

  // Room bookings: date range + optional buildingId when room filter is active.
  // Show active room requests while excluding cancelled/draft/completed events.
  // Caching is disabled — every view/date/filter change must hit the network.
  const {
    data: roomEventsResponse,
    isFetching: isRoomEventsFetching,
    refetch: refetchRoomEvents,
  } = useGetAllPlannerEventsQuery(
    activeTab === 'rooms'
      ? {
          startDate: eventsDateRange.startDate,
          endDate: eventsDateRange.endDate,
          pageSize: 100,
          status: ROOM_CALENDAR_STATUS_FILTER,
          ...(roomBuildingFilter ? { buildingId: roomBuildingFilter } : {}),
        }
      : skipToken,
    { refetchOnMountOrArgChange: true, refetchOnReconnect: true }
  );

  // All spaces as room columns
  const spaceRooms: BaseRoomInfo[] = useMemo(() => {
    return spacesData.map((s) => {
      return {
        spaceId: s.spaceId as string,
        spaceName: s.spaceName,
        buildingId: s.buildingId as string,
      };
    });
  }, [spacesData]);

  // Match events to spaces by spaceId, or by buildingId if no room assigned
  const { allRoomBookings, bookingToEventId, eventIdToSummary } = useMemo(() => {
    if (!roomEventsResponse || !roomEventsResponse.success)
      return {
        allRoomBookings: [] as BaseRoomBooking[],
        bookingToEventId: new Map<string, string>(),
        eventIdToSummary: new Map<string, EventSummary>(),
      };
    const summaries = (roomEventsResponse.data as unknown as EventSummary[]).filter(
      isVisibleRoomCalendarEvent
    );
    const fallbackTitle = t('calendar.untitledEvent');
    const { bookings, bookingToEventId } = mapSummariesToRoomBookings(
      summaries,
      spaceRooms,
      fallbackTitle
    );
    const eventIdToSummary = new Map<string, EventSummary>();
    for (const s of summaries) eventIdToSummary.set(s.eventId as unknown as string, s);
    return { allRoomBookings: bookings, bookingToEventId, eventIdToSummary };
  }, [roomEventsResponse, spaceRooms, t]);

  const allRooms: BaseRoomInfo[] = spaceRooms;

  const handleViewChange = useCallback(
    (_: unknown, data: OptionOnSelectData) => {
      const val = data.optionValue as CalendarView;
      if (val) setView(val);
    },
    [setView]
  );

  const handleRoomViewChange = useCallback(
    (_: unknown, data: OptionOnSelectData) => {
      const val = data.optionValue as CalendarView;
      if (val) setRoomView(val);
    },
    [setRoomView]
  );

  const handleApplyRoomFilters = useCallback(
    (next: RoomFilters) => {
      setRoomFilters(next);
      setSelectedRooms(next.roomIds);
      setIsRoomFilterOpen(false);
    },
    [setSelectedRooms, setIsRoomFilterOpen]
  );

  const handleDayClick = useCallback(
    (day: Date) => {
      setDate(day);
      setView('day');
    },
    [setDate, setView]
  );

  const handleMiniDateSelect = useCallback(
    (day: Date) => {
      setDate(day);
      setMiniCalMonth(day);
    },
    [setDate]
  );

  const handleMiniMonthChange = useCallback(
    (month: Date) => {
      setMiniCalMonth(month);
      setDate(month);
    },
    [setDate]
  );

  const handleDatePickerSelect = useCallback(
    (selectedDate: Date) => {
      setDate(selectedDate);
      setMiniCalMonth(selectedDate);
      setIsDatePickerOpen(false);
    },
    [setDate]
  );

  const headerLabel = getHeaderLabel(date, view);

  const getEventColor = useCallback(
    (event: CalendarEvent) =>
      highlightedEventId && event.id === highlightedEventId
        ? HIGHLIGHTED_EVENT_COLOR
        : getStatusStyle(event.resource.status),
    [highlightedEventId]
  );

  const getEventSubtitle = useCallback((event: CalendarEvent) => {
    const time = event.allDay ? 'All Day' : formatTime(event.start);
    return event.resource.location ? `${time} · ${event.resource.location}` : time;
  }, []);

  const getEventRoom = useCallback((event: CalendarEvent) => event.resource.room || undefined, []);

  const getEventAdditionalRooms = useCallback(
    (event: CalendarEvent) => event.resource.additionalRooms ?? [],
    []
  );

  const getRoomBookingColor = useCallback(
    (booking: BaseRoomBooking) => {
      const eventId = bookingToEventId.get(booking.id);
      const summary = eventId ? eventIdToSummary.get(eventId) : undefined;
      return summary
        ? getStatusStyle(eventStateToStatus(summary.eventState))
        : getStatusStyle('draft');
    },
    [bookingToEventId, eventIdToSummary]
  );

  /** Open the same review-panel drawer used on the Planner Event List page
   *  when an event chip is clicked on the calendar. */
  const reviewActions = useReviewActions();
  const panelTriggerRef = useRef<HTMLElement | null>(null);
  const [selectedReviewDisplayTitle, setSelectedReviewDisplayTitle] = useState<
    string | undefined
  >();
  const [cancelSuccessOpen, setCancelSuccessOpen] = useState(false);
  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      panelTriggerRef.current = document.activeElement as HTMLElement | null;
      setSelectedReviewDisplayTitle(event.title);
      reviewActions.openPanel(event.id);
    },
    [reviewActions]
  );

  const handleRoomBookingClick = useCallback(
    (booking: BaseRoomBooking) => {
      panelTriggerRef.current = document.activeElement as HTMLElement | null;
      const eventId = bookingToEventId.get(booking.id) ?? booking.id;
      setSelectedReviewDisplayTitle(booking.eventTitle);
      reviewActions.openPanel(eventId);
    },
    [reviewActions, bookingToEventId]
  );
  const handleConfirmEvent = useCallback(() => {
    void reviewActions.confirmEvent();
  }, [reviewActions]);
  const handleDeclineEvent = useCallback(
    (reason: string) => {
      void reviewActions.declineEvent(reason);
    },
    [reviewActions]
  );
  const handleSaveEdit = useCallback(
    async (input: import('@/types/planner.types').EventReviewEditInput) => {
      const didSave = await reviewActions.saveEdit(input);
      if (didSave) {
        const refetch = activeTab === 'rooms' ? refetchRoomEvents : refetchCalendarEvents;
        void refetch();
        setTimeout(() => {
          void refetch();
        }, 1000);
      }
      return didSave;
    },
    [reviewActions, activeTab, refetchCalendarEvents, refetchRoomEvents]
  );
  const handleCancelEvent = useCallback(
    async (reason: string) => {
      const didCancel = await reviewActions.cancelEvent(reason);
      if (didCancel) {
        setCancelSuccessOpen(true);
      }
    },
    [reviewActions]
  );
  const handleConfirmCancellation = useCallback(async () => {
    const didCancel = await reviewActions.confirmCancellation();
    if (didCancel) {
      setCancelSuccessOpen(true);
    }
  }, [reviewActions]);
  const handleNoOccurrence = useCallback(() => {
    void reviewActions.markNoOccurrence();
  }, [reviewActions]);
  const handleSpaceChange = useCallback(
    (spaceId: string) => reviewActions.setSelectedSpace(spaceId),
    [reviewActions]
  );

  const handleEventDrop = useCallback((info: EventDropInfo<CalendarEvent>) => {
    setPendingDrop(info);
  }, []);

  const dropClaimWarning = useMemo<
    { kind: 'unclaimed' } | { kind: 'other'; name?: string } | null
  >(() => {
    if (!pendingDrop) return null;
    const assignedEmail = pendingDrop.event.resource.plannerAssignedEmail?.toLowerCase();
    if (!assignedEmail) return { kind: 'unclaimed' };
    const currentEmail = getActiveAccount()?.username?.toLowerCase();
    if (currentEmail && assignedEmail === currentEmail) return null;
    return { kind: 'other', name: pendingDrop.event.resource.plannerAssignedName };
  }, [pendingDrop]);

  const handleConfirmDrop = useCallback(async () => {
    if (!pendingDrop) return;
    setIsPatching(true);
    const startTime = Date.now();
    try {
      const detailResult = await getEventById({
        eventId: pendingDrop.event.id as unknown as import('@/store/models/ids').EventId,
      }).unwrap();
      const etag = detailResult.etag;

      await patchEvent({
        params: {
          eventId: pendingDrop.event.id as unknown as import('@/store/models/ids').EventId,
          buildingId: pendingDrop.event.resource.buildingId as unknown as
            | import('@/store/models/ids').BuildingId
            | undefined,
          etag,
        },
        body: {
          eventStartDate: pendingDrop.newStart.toISOString(),
          eventEndDate: pendingDrop.newEnd.toISOString(),
        },
      }).unwrap();
      telemetry.trackEvent('Calendar', EVENT_TYPES.SUCCESS, {
        operationName: 'DragDropReschedule',
        eventId: pendingDrop.event.id,
        fromView: view,
        durationMs: String(Date.now() - startTime),
      });
    } catch {
      telemetry.trackEvent('Calendar', EVENT_TYPES.FAILURE, {
        operationName: 'DragDropReschedule',
        eventId: pendingDrop.event.id,
        fromView: view,
        durationMs: String(Date.now() - startTime),
      });
      dispatchToast(
        <Toast>
          <ToastTitle>{t('calendar.dragDrop.error')}</ToastTitle>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setIsPatching(false);
      setPendingDrop(null);
    }
  }, [pendingDrop, patchEvent, getEventById, view, dispatchToast, t]);

  const handleCancelDrop = useCallback(() => {
    setPendingDrop(null);
  }, []);

  const handleRoomBookingDrop = useCallback(
    (info: RoomBookingDropInfo<BaseRoomInfo, BaseRoomBooking>) => {
      setPendingRoomDrop(info);
      setSelectedRoomSetupCodes([]);
      setRoomDropAvailability('checking');

      const eventId = bookingToEventId.get(info.booking.id) ?? info.booking.id;
      const start = info.newStart;
      const end = info.newEnd;
      const startDate = format(start, 'yyyy-MM-dd');
      const endDate = format(end, 'yyyy-MM-dd');
      const startTime = format(start, 'HH:mm');
      const endTime = format(end, 'HH:mm');
      const buildingId = info.toRoom.buildingId;
      const spaceId = info.toRoom.spaceId;

      if (!buildingId || !spaceId) {
        setRoomDropAvailability('unavailable');
        return;
      }

      // Multi-room events must stay within a single building; block cross-building room moves.
      const summary = eventIdToSummary.get(eventId);
      const isMultiRoom = (summary?.spaces?.length ?? 0) > 1;
      const roomChanged = info.fromRoom?.spaceId !== info.toRoom.spaceId;
      if (
        roomChanged &&
        isMultiRoom &&
        summary?.buildingId &&
        (buildingId as unknown as string) !== (summary.buildingId as unknown as string)
      ) {
        setRoomDropAvailability('cross-building');
        return;
      }

      void (async () => {
        try {
          const res = await checkSpaceAvailability({
            spaceId: spaceId as unknown as import('@/store/models/ids').SpaceId,
            body: {
              buildingId: buildingId as unknown as BuildingId,
              startDate,
              endDate,
              startTime,
              endTime,
              excludeEventId: eventId as unknown as import('@/store/models/ids').EventId,
            },
          }).unwrap();
          if (res.success && res.data?.isAvailable) {
            setRoomDropAvailability('available');
          } else {
            setRoomDropAvailability('unavailable');
          }
        } catch {
          setRoomDropAvailability('unavailable');
        }
      })();
    },
    [bookingToEventId, eventIdToSummary, checkSpaceAvailability]
  );

  const roomDropClaimWarning = useMemo<
    { kind: 'unclaimed' } | { kind: 'other'; name?: string } | null
  >(() => {
    if (!pendingRoomDrop) return null;
    const eventId = bookingToEventId.get(pendingRoomDrop.booking.id) ?? pendingRoomDrop.booking.id;
    const summary = eventIdToSummary.get(eventId);
    const assignedEmail = summary?.plannerAssignedEmail?.toLowerCase();
    if (!assignedEmail) return { kind: 'unclaimed' };
    const currentEmail = getActiveAccount()?.username?.toLowerCase();
    if (currentEmail && assignedEmail === currentEmail) return null;
    return { kind: 'other', name: summary?.plannerAssignedName };
  }, [pendingRoomDrop, bookingToEventId, eventIdToSummary]);

  const targetRoomSetupOptions = useMemo(() => {
    if (!pendingRoomDrop || pendingRoomDrop.fromRoom?.spaceId === pendingRoomDrop.toRoom.spaceId) {
      return [];
    }
    const targetSpace = findSpaceById(
      spacesData,
      pendingRoomDrop.toRoom.spaceId as unknown as string
    );

    const eventId = bookingToEventId.get(pendingRoomDrop.booking.id) ?? pendingRoomDrop.booking.id;
    const sourceSpaceId = pendingRoomDrop.fromRoom?.spaceId as unknown as string | undefined;
    const sourceSetupCodes = getSetupCodesForSpace(
      eventIdToSummary.get(eventId)?.spaces,
      sourceSpaceId
    );
    return resolveRoomSetupOptions(targetSpace, sourceSetupCodes);
  }, [pendingRoomDrop, spacesData, bookingToEventId, eventIdToSummary]);

  useEffect(() => {
    const onlySetupCode = getOnlyAvailableSetupCode(roomDropAvailability, targetRoomSetupOptions);
    if (onlySetupCode) {
      setSelectedRoomSetupCodes([onlySetupCode]);
    }
  }, [roomDropAvailability, targetRoomSetupOptions]);

  const targetRoomSpace = useMemo(
    () =>
      pendingRoomDrop
        ? findSpaceById(spacesData, pendingRoomDrop.toRoom.spaceId as unknown as string)
        : undefined,
    [pendingRoomDrop, spacesData]
  );

  const isRoomDropSetupOverCapacity = useMemo(() => {
    if (!pendingRoomDrop) return false;
    return isSetupSelectionOverCapacity(
      selectedRoomSetupCodes,
      targetRoomSetupOptions,
      targetRoomSpace?.roomCapacity
    );
  }, [pendingRoomDrop, selectedRoomSetupCodes, targetRoomSpace, targetRoomSetupOptions]);

  const handleConfirmRoomDrop = useCallback(async () => {
    if (!pendingRoomDrop) return;
    const eventId = bookingToEventId.get(pendingRoomDrop.booking.id) ?? pendingRoomDrop.booking.id;
    const summary = eventIdToSummary.get(eventId);
    const sourceBuildingId = summary?.buildingId;
    const targetBuildingId = pendingRoomDrop.toRoom.buildingId;
    const roomChanged = pendingRoomDrop.fromRoom?.spaceId !== pendingRoomDrop.toRoom.spaceId;
    const timeChanged =
      pendingRoomDrop.booking.start.getTime() !== pendingRoomDrop.newStart.getTime();
    if (roomChanged && targetRoomSetupOptions.length > 0 && selectedRoomSetupCodes.length === 0)
      return;
    if (isRoomDropSetupOverCapacity) return;

    setIsPatching(true);
    const startTime = Date.now();

    // PATCH spaces replaces the whole collection, so keep the event's other rooms and swap only the dragged one.
    const fromSpaceId = pendingRoomDrop.fromRoom?.spaceId as unknown as string | undefined;
    const existingSpaces = summary?.spaces ?? [];
    const movedEntry = {
      buildingId: targetBuildingId as unknown as BuildingId,
      spaceId: pendingRoomDrop.toRoom.spaceId as unknown as import('@/store/models/ids').SpaceId,
      spaceName: pendingRoomDrop.toRoom.spaceName,
      ...(roomChanged && selectedRoomSetupCodes.length > 0
        ? { selectedSetupCodes: selectedRoomSetupCodes }
        : {}),
    };
    const matched = existingSpaces.some((s) => (s.spaceId as unknown as string) === fromSpaceId);
    const nextSpaces =
      existingSpaces.length > 1 && matched
        ? existingSpaces.map((s) =>
            (s.spaceId as unknown as string) === fromSpaceId
              ? movedEntry
              : {
                  buildingId: sourceBuildingId as unknown as BuildingId,
                  spaceId: s.spaceId as unknown as import('@/store/models/ids').SpaceId,
                  spaceName: s.spaceName,
                  ...(s.selectedSetupCodes?.length
                    ? { selectedSetupCodes: s.selectedSetupCodes }
                    : {}),
                }
          )
        : [movedEntry];
    try {
      const detailResult = await getEventById({
        eventId: eventId as unknown as import('@/store/models/ids').EventId,
      }).unwrap();
      const etag = detailResult.etag;

      await patchEvent({
        params: {
          eventId: eventId as unknown as import('@/store/models/ids').EventId,
          buildingId: sourceBuildingId,
          etag,
        },
        body: {
          ...(timeChanged
            ? {
                eventStartDate: pendingRoomDrop.newStart.toISOString(),
                eventEndDate: pendingRoomDrop.newEnd.toISOString(),
              }
            : {}),
          ...(roomChanged ? { spaces: nextSpaces } : {}),
        },
      }).unwrap();
      telemetry.trackEvent('Calendar', EVENT_TYPES.SUCCESS, {
        operationName: 'DragDropRoomMove',
        eventId,
        durationMs: String(Date.now() - startTime),
      });
    } catch {
      telemetry.trackEvent('Calendar', EVENT_TYPES.FAILURE, {
        operationName: 'DragDropRoomMove',
        eventId,
        durationMs: String(Date.now() - startTime),
      });
      dispatchToast(
        <Toast>
          <ToastTitle>{t('calendar.roomDragDrop.error')}</ToastTitle>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setIsPatching(false);
      setPendingRoomDrop(null);
      setSelectedRoomSetupCodes([]);
      setRoomDropAvailability('idle');
    }
  }, [
    pendingRoomDrop,
    bookingToEventId,
    eventIdToSummary,
    getEventById,
    patchEvent,
    dispatchToast,
    selectedRoomSetupCodes,
    targetRoomSetupOptions.length,
    isRoomDropSetupOverCapacity,
    t,
  ]);

  const handleCancelRoomDrop = useCallback(() => {
    setPendingRoomDrop(null);
    setSelectedRoomSetupCodes([]);
    setRoomDropAvailability('idle');
  }, []);

  const filteredRooms: BaseRoomInfo[] = useMemo(() => {
    let rooms = allRooms;
    if (roomFilters.buildingIds.length > 0) {
      const buildingSet = new Set(roomFilters.buildingIds);
      rooms = rooms.filter((r) => r.buildingId && buildingSet.has(r.buildingId));
    }
    if (selectedRooms.length > 0) {
      rooms = rooms.filter((r) => selectedRooms.includes(r.spaceId));
    }

    // Sort rooms with bookings first so they appear in the initial visible columns
    const roomsWithBookings = new Set(allRoomBookings.map((b) => b.roomId));
    return [...rooms].sort((a, b) => {
      const aHas = roomsWithBookings.has(a.spaceId) ? 0 : 1;
      const bHas = roomsWithBookings.has(b.spaceId) ? 0 : 1;
      return aHas - bHas;
    });
  }, [allRooms, selectedRooms, roomFilters.buildingIds, allRoomBookings]);

  const filteredRoomBookings: BaseRoomBooking[] = useMemo(() => {
    const roomSet = new Set(filteredRooms.map((r) => r.spaceId));
    if (roomSet.size === allRooms.length) return allRoomBookings;
    return allRoomBookings.filter((b) => roomSet.has(b.roomId));
  }, [allRoomBookings, filteredRooms, allRooms]);

  return (
    <div className={styles.root}>
      {/* Page header: title + subtitle */}
      <div className={styles.pageHeader}>
        <div>
          <Text className={styles.pageTitle}>{t('calendar.title')}</Text>
          <Text className={styles.pageSubtitle}>{t('calendar.subtitle')}</Text>
        </div>
      </div>

      {/* Tabs: Events | Rooms */}
      <TabList
        selectedValue={activeTab}
        onTabSelect={(_: SelectTabEvent, data: SelectTabData) =>
          setActiveTab(data.value as CalendarTab)
        }
        className={styles.tabList}
      >
        <Tab value="events" icon={<CalendarLtrRegular />}>
          Events
        </Tab>
        <Tab value="rooms" icon={<ConferenceRoomRegular />}>
          Rooms
        </Tab>
      </TabList>

      {/* Unified white card */}
      {activeTab === 'events' && (
        <div className={styles.card}>
          {/* Toolbar: Today | < > | date range ∨ | spacer | Filter icon + All Statuses | Month view ∨ */}
          <div className={styles.toolbar}>
            <BasicButton
              variant="outline"
              size="small"
              className={styles.todayButton}
              onClick={() => {
                const now = new Date();
                setDate(now);
                setMiniCalMonth(now);
              }}
            >
              {t('calendar.today')}
            </BasicButton>
            <BasicButton
              variant="subtle"
              size="small"
              icon={<ChevronLeftRegular />}
              className={styles.navButton}
              onClick={() => setDate(navigatePrev(date, view))}
              aria-label={t('calendar.previous')}
            />
            <BasicButton
              variant="subtle"
              size="small"
              icon={<ChevronRightRegular />}
              className={styles.navButton}
              onClick={() => setDate(navigateNext(date, view))}
              aria-label={t('calendar.next')}
            />
            <Popover
              open={isDatePickerOpen}
              onOpenChange={(_, data) => setIsDatePickerOpen(data.open)}
              positioning="below-start"
            >
              <PopoverTrigger disableButtonEnhancement>
                <button type="button" className={styles.navLabelButton}>
                  <span>{headerLabel}</span>
                  <ChevronDownRegular className={styles.navLabelChevron} />
                </button>
              </PopoverTrigger>
              <PopoverSurface>
                <FluentCalendar
                  value={date}
                  onSelectDate={handleDatePickerSelect}
                  showGoToToday={false}
                />
              </PopoverSurface>
            </Popover>
            <div className={styles.toolbarSpacer} />
            {eventsFilterSlot}
            <Dropdown
              value={t(`calendar.views.${view}`)}
              selectedOptions={[view]}
              onOptionSelect={handleViewChange}
              className={styles.viewDropdown}
              aria-label={t('calendar.viewSelector')}
            >
              {VIEW_OPTIONS.map((v) => (
                <Option key={v} value={v} text={t(`calendar.views.${v}`)}>
                  {VIEW_ICONS[v]} {t(`calendar.views.${v}`)}
                </Option>
              ))}
            </Dropdown>
          </div>

          {/* Body: mini calendar + main grid */}
          <div className={styles.bodyLayout}>
            <div className={styles.sidebar}>
              <MiniCalendar
                displayMonth={miniCalMonth}
                selectedDate={date}
                onMonthChange={handleMiniMonthChange}
                onDateSelect={handleMiniDateSelect}
                weekHighlight={view === 'week'}
              />
            </div>

            <div className={styles.calendarArea}>
              {isEventsFetching && (
                <div className={styles.loadingOverlay}>
                  <Spinner size="medium" />
                </div>
              )}
              {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
              <div
                className={styles.calendarWrapper}
                tabIndex={0}
                role="region"
                aria-label={t('calendar.calendarGrid')}
              >
                {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
                <Calendar
                  view={view}
                  date={date}
                  events={displayEvents}
                  getEventColor={getEventColor}
                  getEventSubtitle={getEventSubtitle}
                  getEventRoom={getEventRoom}
                  getEventAdditionalRooms={getEventAdditionalRooms}
                  onDayClick={handleDayClick}
                  onEventClick={handleEventClick}
                  onEventDrop={handleEventDrop}
                  scrollToTime={scrollToTime}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'rooms' && (
        <div className={styles.card}>
          {/* Toolbar for rooms view */}
          <div className={styles.toolbar}>
            <BasicButton
              variant="outline"
              size="small"
              className={styles.todayButton}
              onClick={() => {
                const now = new Date();
                setDate(now);
                setMiniCalMonth(now);
              }}
            >
              {t('calendar.today')}
            </BasicButton>
            <BasicButton
              variant="subtle"
              size="small"
              icon={<ChevronLeftRegular />}
              className={styles.navButton}
              onClick={() => setDate(navigatePrev(date, roomView))}
              aria-label={t('calendar.previous')}
            />
            <BasicButton
              variant="subtle"
              size="small"
              icon={<ChevronRightRegular />}
              className={styles.navButton}
              onClick={() => setDate(navigateNext(date, roomView))}
              aria-label={t('calendar.next')}
            />
            <Popover
              open={isDatePickerOpen}
              onOpenChange={(_, data) => setIsDatePickerOpen(data.open)}
              positioning="below-start"
            >
              <PopoverTrigger disableButtonEnhancement>
                <button type="button" className={styles.navLabelButton}>
                  <span>{getHeaderLabel(date, roomView)}</span>
                  <ChevronDownRegular className={styles.navLabelChevron} />
                </button>
              </PopoverTrigger>
              <PopoverSurface>
                <FluentCalendar
                  value={date}
                  onSelectDate={handleDatePickerSelect}
                  showGoToToday={false}
                />
              </PopoverSurface>
            </Popover>
            <div className={styles.toolbarSpacer} />
            <BasicButton
              variant="outline"
              size="small"
              icon={<FilterRegular />}
              className={mergeClasses(
                styles.todayButton,
                hasActiveRoomFilters && styles.filterButtonActive
              )}
              onClick={() => setIsRoomFilterOpen(true)}
            >
              Filter
            </BasicButton>
            <Dropdown
              value={t(`calendar.views.${roomView}`)}
              selectedOptions={[roomView]}
              onOptionSelect={handleRoomViewChange}
              className={styles.viewDropdown}
              aria-label={t('calendar.viewSelector')}
            >
              {ROOM_VIEW_OPTIONS.map((v) => (
                <Option key={v} value={v} text={t(`calendar.views.${v}`)}>
                  {VIEW_ICONS[v]} {t(`calendar.views.${v}`)}
                </Option>
              ))}
            </Dropdown>
          </div>

          {/* Body: mini calendar + room grid */}
          <div className={styles.bodyLayout}>
            <div className={styles.sidebar}>
              <MiniCalendar
                displayMonth={miniCalMonth}
                selectedDate={date}
                onMonthChange={handleMiniMonthChange}
                onDateSelect={handleMiniDateSelect}
                weekHighlight={roomView === 'week'}
              />
            </div>

            <div className={styles.calendarArea}>
              {isRoomEventsFetching && (
                <div className={styles.loadingOverlay}>
                  <Spinner size="medium" />
                </div>
              )}
              {/* eslint-disable jsx-a11y/no-noninteractive-tabindex */}
              <div
                className={styles.calendarWrapper}
                tabIndex={0}
                role="region"
                aria-label={t('calendar.roomCalendarGrid')}
              >
                {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
                {roomView === 'month' && (
                  <RoomMonthView
                    date={date}
                    rooms={filteredRooms}
                    bookings={filteredRoomBookings}
                    onDayClick={(day) => {
                      setDate(day);
                      setRoomView('day');
                    }}
                  />
                )}
                {roomView === 'week' && (
                  <RoomWeekGrid
                    date={date}
                    rooms={filteredRooms}
                    bookings={filteredRoomBookings}
                    getBookingColor={getRoomBookingColor}
                    onSelectBooking={handleRoomBookingClick}
                    onBookingDrop={handleRoomBookingDrop}
                  />
                )}
                {roomView === 'day' && (
                  <RoomCalendar
                    view={roomView}
                    date={date}
                    rooms={filteredRooms}
                    bookings={filteredRoomBookings}
                    getBookingColor={getRoomBookingColor}
                    onBookingClick={handleRoomBookingClick}
                    onBookingDrop={handleRoomBookingDrop}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <RoomFilterPanel
        open={isRoomFilterOpen}
        appliedFilters={roomFilters}
        availableRooms={allRooms}
        buildingNameById={buildingNameById}
        onApply={handleApplyRoomFilters}
        onCancel={() => setIsRoomFilterOpen(false)}
      />

      {/* Same review-panel drawer used on the Planner Event List page,
          opened when an event chip is clicked on the calendar. */}
      <EventReviewPanel
        open={reviewActions.panelOpen}
        event={reviewActions.event}
        displayTitle={selectedReviewDisplayTitle}
        building={reviewActions.building}
        spaces={
          reviewActions.spaces as unknown as import('@/store/models/spaces').SpaceWithAvailability[]
        }
        selectedSpaceStateProvince={reviewActions.selectedSpaceStateProvince}
        isLoading={reviewActions.isLoading}
        isError={reviewActions.isError}
        selectedSpaceId={reviewActions.selectedSpaceId}
        selectedSpaces={reviewActions.selectedSpaces}
        onSelectedSpacesChange={reviewActions.setSelectedSpaces}
        isConfirming={reviewActions.isConfirming}
        isDeclining={reviewActions.isDeclining}
        isMarkingNoOccurrence={reviewActions.isMarkingNoOccurrence}
        isSavingEdit={reviewActions.isSavingEdit}
        error={reviewActions.error}
        onClose={() => {
          reviewActions.closePanel();
          setSelectedReviewDisplayTitle(undefined);
          requestAnimationFrame(() => {
            panelTriggerRef.current?.focus();
            panelTriggerRef.current = null;
          });
        }}
        onConfirm={handleConfirmEvent}
        onDeclineClick={reviewActions.openDeclineDialog}
        onCancelClick={() => {
          if (reviewActions.event?.eventState === 'SUBMITTED_FOR_CANCELLATION') {
            void handleConfirmCancellation();
          } else {
            reviewActions.openCancelDialog();
          }
        }}
        onNoOccurrenceClick={handleNoOccurrence}
        onSaveEdit={handleSaveEdit}
        onSpaceChange={handleSpaceChange}
        onFetchSpaces={reviewActions.fetchSpacesAvailability}
        isSearchingSpaces={reviewActions.isSearchingSpaces}
        onResendSurvey={reviewActions.resendSurvey}
        isResendingSurvey={reviewActions.isResendingSurvey}
        onRetry={reviewActions.refetch}
      />
      <DeclineDialog
        open={reviewActions.declineDialogOpen}
        isProcessing={reviewActions.isDeclining}
        error={reviewActions.error}
        onConfirm={handleDeclineEvent}
        onCancel={reviewActions.closeDeclineDialog}
      />
      <CancelBookingDialog
        open={reviewActions.cancelDialogOpen}
        eventTitle={selectedReviewDisplayTitle ?? reviewActions.event?.eventTitle ?? ''}
        isProcessing={reviewActions.isCancelling}
        error={reviewActions.error}
        onConfirm={(reason: string) => void handleCancelEvent(reason)}
        onCancel={reviewActions.closeCancelDialog}
      />
      <CancelSuccessDialog open={cancelSuccessOpen} onClose={() => setCancelSuccessOpen(false)} />

      {/* Drag-drop reschedule confirmation dialog */}
      <Dialog open={!!pendingDrop} modalType="modal">
        <DialogSurface aria-describedby="drag-drop-confirm-description">
          <DialogBody>
            <DialogTitle>{t('calendar.dragDrop.confirmTitle')}</DialogTitle>
            <DialogContent id="drag-drop-confirm-description">
              <Text>
                {pendingDrop
                  ? t('calendar.dragDrop.confirmMessage', {
                      title: pendingDrop.event.title,
                      date: format(pendingDrop.newStart, 'PPp'),
                    })
                  : ''}
              </Text>
              {dropClaimWarning && (
                <Text
                  block
                  style={{
                    marginTop: tokens.spacingVerticalS,
                    color: tokens.colorPaletteDarkOrangeForeground1,
                  }}
                >
                  {dropClaimWarning.kind === 'unclaimed'
                    ? t('calendar.dragDrop.unclaimedWarning')
                    : dropClaimWarning.name
                      ? t('calendar.dragDrop.claimedByOtherWarning', {
                          name: dropClaimWarning.name,
                        })
                      : t('calendar.dragDrop.claimedByOtherUnknownWarning')}
                </Text>
              )}
              {isPatching && (
                <Spinner size="tiny" style={{ marginLeft: tokens.spacingHorizontalS }} />
              )}
            </DialogContent>
            <DialogActions>
              <BasicButton
                variant="primary"
                size="medium"
                onClick={() => void handleConfirmDrop()}
                disabled={isPatching || !!dropClaimWarning}
              >
                {t('calendar.dragDrop.confirm')}
              </BasicButton>
              <BasicButton
                variant="secondary"
                size="medium"
                onClick={handleCancelDrop}
                disabled={isPatching}
              >
                {t('calendar.dragDrop.cancel')}
              </BasicButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Drag-drop room-move confirmation dialog (rooms tab) */}
      <Dialog open={!!pendingRoomDrop} modalType="modal">
        <DialogSurface aria-describedby="room-drag-drop-confirm-description">
          <DialogBody>
            <DialogTitle>{t('calendar.roomDragDrop.confirmTitle')}</DialogTitle>
            <DialogContent id="room-drag-drop-confirm-description">
              <Text>
                {pendingRoomDrop
                  ? pendingRoomDrop.fromRoom?.spaceId === pendingRoomDrop.toRoom.spaceId
                    ? t('calendar.roomDragDrop.confirmMessageReschedule', {
                        title: pendingRoomDrop.booking.eventTitle,
                        date: format(pendingRoomDrop.newStart, 'PPp'),
                      })
                    : t('calendar.roomDragDrop.confirmMessage', {
                        title: pendingRoomDrop.booking.eventTitle,
                        fromRoom: pendingRoomDrop.fromRoom?.spaceName ?? '—',
                        toRoom: pendingRoomDrop.toRoom.spaceName,
                        date: format(pendingRoomDrop.newStart, 'PPp'),
                      })
                  : ''}
              </Text>
              {roomDropAvailability === 'available' &&
                pendingRoomDrop &&
                pendingRoomDrop.fromRoom?.spaceId !== pendingRoomDrop.toRoom.spaceId &&
                targetRoomSetupOptions.length > 0 && (
                  <Field
                    label={t('calendar.roomDragDrop.setupStyleLabel')}
                    required
                    style={{ marginTop: tokens.spacingVerticalM }}
                  >
                    <Dropdown
                      multiselect
                      aria-label={t('calendar.roomDragDrop.setupStyleLabel')}
                      placeholder={t('calendar.roomDragDrop.setupStylePlaceholder')}
                      value={selectedRoomSetupCodes
                        .map((code) => getRoomLayoutMeta(code).title)
                        .join(', ')}
                      selectedOptions={selectedRoomSetupCodes}
                      onOptionSelect={(_event, data) =>
                        setSelectedRoomSetupCodes(data.selectedOptions)
                      }
                    >
                      {targetRoomSetupOptions.map((layout) => {
                        const title = getRoomLayoutMeta(layout.code).title;
                        const label = layout.capacity > 0 ? `${title} (${layout.capacity})` : title;
                        return (
                          <Option key={layout.code} value={layout.code} text={title}>
                            {label}
                          </Option>
                        );
                      })}
                    </Dropdown>
                  </Field>
                )}
              {isRoomDropSetupOverCapacity && (
                <Text
                  block
                  role="alert"
                  style={{
                    marginTop: tokens.spacingVerticalS,
                    color: tokens.colorPaletteRedForeground1,
                  }}
                >
                  <strong>{t('spaceCard.capacityWarningPrefix', { ns: 'spaceCard' })}</strong>{' '}
                  {t('calendar.roomDragDrop.capacityWarningMessage', {
                    capacity: targetRoomSpace?.roomCapacity,
                  })}
                </Text>
              )}
              {roomDropAvailability === 'checking' && (
                <Text block style={{ marginTop: tokens.spacingVerticalS }}>
                  {t('calendar.roomDragDrop.checkingAvailability')}
                  <Spinner size="tiny" style={{ marginLeft: tokens.spacingHorizontalS }} />
                </Text>
              )}
              {roomDropAvailability === 'unavailable' && (
                <Text
                  block
                  style={{
                    marginTop: tokens.spacingVerticalS,
                    color: tokens.colorPaletteRedForeground1,
                  }}
                >
                  {t('calendar.roomDragDrop.unavailableWarning')}
                </Text>
              )}
              {roomDropAvailability === 'cross-building' && (
                <Text
                  block
                  style={{
                    marginTop: tokens.spacingVerticalS,
                    color: tokens.colorPaletteRedForeground1,
                  }}
                >
                  {t('calendar.roomDragDrop.crossBuildingWarning')}
                </Text>
              )}
              {roomDropClaimWarning && (
                <Text
                  block
                  style={{
                    marginTop: tokens.spacingVerticalS,
                    color: tokens.colorPaletteDarkOrangeForeground1,
                  }}
                >
                  {roomDropClaimWarning.kind === 'unclaimed'
                    ? t('calendar.dragDrop.unclaimedWarning')
                    : roomDropClaimWarning.name
                      ? t('calendar.dragDrop.claimedByOtherWarning', {
                          name: roomDropClaimWarning.name,
                        })
                      : t('calendar.dragDrop.claimedByOtherUnknownWarning')}
                </Text>
              )}
              {isPatching && (
                <Spinner size="tiny" style={{ marginLeft: tokens.spacingHorizontalS }} />
              )}
            </DialogContent>
            <DialogActions>
              <BasicButton
                variant="primary"
                size="medium"
                onClick={() => void handleConfirmRoomDrop()}
                disabled={
                  isPatching ||
                  !!roomDropClaimWarning ||
                  roomDropAvailability !== 'available' ||
                  (!!pendingRoomDrop &&
                    pendingRoomDrop.fromRoom?.spaceId !== pendingRoomDrop.toRoom.spaceId &&
                    targetRoomSetupOptions.length > 0 &&
                    selectedRoomSetupCodes.length === 0) ||
                  isRoomDropSetupOverCapacity
                }
              >
                {t('calendar.roomDragDrop.confirm')}
              </BasicButton>
              <BasicButton
                variant="secondary"
                size="medium"
                onClick={handleCancelRoomDrop}
                disabled={isPatching}
              >
                {t('calendar.roomDragDrop.cancel')}
              </BasicButton>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Toaster toasterId={toasterId} />
    </div>
  );
};

CalendarScreen.displayName = 'CalendarScreen';
